import {
  CFormCheck,
  CFormInput,
  CFormSelect,
  CFormTextarea,
} from "@coreui/react";
import { useEffect, useState } from "react";
import { Controller } from "react-hook-form";
import { Field, FormSection } from "../../components/ui/FormUi";
import FileUploaderV2 from "../../components/ui/FileUploaderV2";
import InfiniteScrollSelect from "../../components/ui/InfiniteScrollSelect";
import { PROMO_RESTRICTION_TYPESV2 } from "../../rules/promo";

export default function CreateRewardForm({
  control,
  setValue,
  watch,
  data,
  mode,
}: any) {
  const promoScope = watch("promoScope");
  const type = watch("type");
  const [hasNewImage, setHasNewImage] = useState(false);

  useEffect(() => {
    if (promoScope !== "EVENT") setValue("event", null);
    if (promoScope !== "SPECIALS") setValue("offer", null);
    if (promoScope !== "PLACE") setValue("place", null);
    if (promoScope !== "GLOBAL") setValue("restrictionType", "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promoScope]);

  useEffect(() => {
    if (mode === "create" && type === "BOOKING") {
      setValue("promoScope", "GLOBAL");
      setValue("restrictionType", "NONE");
    }
  }, [type, mode, setValue]);

  useEffect(() => {
    if (mode === "edit" && data?.picture) {
      setValue("picture", data.picture, {
        shouldValidate: true,
        shouldDirty: false,
      });
    }
  }, [mode, data?.picture, setValue]);

  return (
    <>
      {/* General */}
      <FormSection title="Informations générales">
        <Controller
          name="title"
          control={control}
          render={({ field, fieldState }) => (
            <Field
              label="Titre"
              htmlFor="title"
              required
              error={fieldState.error?.message}
            >
              <CFormInput
                id="title"
                placeholder="Titre"
                {...field}
                value={field.value ?? ""}
              />
            </Field>
          )}
        />

        <Controller
          name="type"
          control={control}
          defaultValue="OFFER"
          render={({ field, fieldState }) => (
            <Field
              label="Type de bonus"
              htmlFor="type"
              required
              error={fieldState.error?.message}
            >
              <CFormSelect id="type" {...field} value={field.value ?? ""}>
                <option value="">Sélectionner un type</option>
                <option value="BOOKING">Réservation</option>
                <option value="RIDE">Trajet</option>
              </CFormSelect>
            </Field>
          )}
        />

        <Controller
          name="description"
          control={control}
          render={({ field, fieldState }) => (
            <Field
              col="col-12"
              label="Description"
              htmlFor="description"
              error={fieldState.error?.message}
            >
              <CFormTextarea
                id="description"
                rows={4}
                placeholder="Décrivez les conditions de la récompense..."
                {...field}
                value={field.value ?? ""}
              />
            </Field>
          )}
        />

        <Controller
          name="picture"
          control={control}
          render={({ field, fieldState }) => (
            <Field
              col="col-12"
              label="Image"
              required
              error={fieldState.error?.message}
            >
              <FileUploaderV2
                name="picture"
                accept="image/png,image/jpeg,image/jpg"
                existingUrl={
                  mode === "edit" && !hasNewImage ? data?.picture : undefined
                }
                file={field.value}
                onChange={(file: File | null) => {
                  setHasNewImage(true);
                  setValue("picture", file, {
                    shouldDirty: true,
                    shouldTouch: true,
                  });
                }}
              />
            </Field>
          )}
        />
      </FormSection>

      {/* Reward */}
      <FormSection title="Récompense">
        <Controller
          name="pointsRequired"
          control={control}
          render={({ field, fieldState }) => (
            <Field
              label="Points requis"
              htmlFor="pointsRequired"
              required
              error={fieldState.error?.message}
            >
              <CFormInput
                id="pointsRequired"
                type="number"
                placeholder="Points"
                {...field}
                value={field.value ?? ""}
              />
            </Field>
          )}
        />

        <Controller
          name="discount"
          control={control}
          render={({ field, fieldState }) => (
            <Field
              label="Remise (%)"
              htmlFor="discount"
              required
              error={fieldState.error?.message}
            >
              <CFormInput
                id="discount"
                type="number"
                placeholder="Remise"
                {...field}
                value={field.value ?? ""}
              />
            </Field>
          )}
        />

        <Controller
          name="maxUses"
          control={control}
          render={({ field, fieldState }) => (
            <Field
              label="Utilisations maximales"
              htmlFor="maxUses"
              required
              error={fieldState.error?.message}
            >
              <CFormInput
                id="maxUses"
                type="number"
                placeholder="Utilisations maximales"
                {...field}
                value={field.value ?? ""}
              />
            </Field>
          )}
        />

        <Controller
          name="expiresAt"
          control={control}
          render={({ field, fieldState }) => (
            <Field
              label="Date d'expiration"
              htmlFor="expiresAt"
              error={fieldState.error?.message}
            >
              <CFormInput
                id="expiresAt"
                type="date"
                {...field}
                value={field.value ?? ""}
              />
            </Field>
          )}
        />
      </FormSection>

      {/* Scope (booking only) */}
      {type === "BOOKING" && (
        <FormSection title="Portée du bonus">
          <Controller
            name="promoScope"
            control={control}
            defaultValue="GLOBAL"
            render={({ field, fieldState }) => (
              <Field
                label="Type de promo"
                htmlFor="promoScope"
                required
                error={fieldState.error?.message}
              >
                <CFormSelect id="promoScope" {...field}>
                  <option value="GLOBAL">Global</option>
                  <option value="EVENT">Événement</option>
                  <option value="SPECIALS">Offre</option>
                  <option value="PLACE">Établissement</option>
                </CFormSelect>
              </Field>
            )}
          />

          {promoScope === "GLOBAL" && (
            <Controller
              name="restrictionType"
              control={control}
              defaultValue="NONE"
              render={({ field, fieldState }) => (
                <Field
                  label="Restreindre à"
                  htmlFor="restrictionType"
                  required
                  error={fieldState.error?.message}
                >
                  <CFormSelect
                    id="restrictionType"
                    {...field}
                    value={field.value ?? ""}
                  >
                    {PROMO_RESTRICTION_TYPESV2.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </CFormSelect>
                  <small className="text-muted">
                    {field.value === ""
                      ? "Le code s'appliquera à tous les événements, offres et lieux"
                      : `Le code s'appliquera uniquement aux ${field.value?.toLowerCase()}s`}
                  </small>
                </Field>
              )}
            />
          )}

          {promoScope === "EVENT" && (
            <Controller
              name="event"
              control={control}
              render={({ field, fieldState }) => (
                <Field
                  label="Événement"
                  required
                  error={fieldState.error?.message}
                >
                  <InfiniteScrollSelect
                    url={{ path: "events/ours" }}
                    getOptionLabel={(o: any) => o.name}
                    getOptionValue={(o: any) => o.id}
                    value={field.value}
                    onChange={field.onChange}
                  />
                </Field>
              )}
            />
          )}

          {promoScope === "SPECIALS" && (
            <Controller
              name="offer"
              control={control}
              render={({ field, fieldState }) => (
                <Field label="Offre" required error={fieldState.error?.message}>
                  <InfiniteScrollSelect
                    url={{ path: "specials/all" }}
                    getOptionLabel={(o: any) => o.title}
                    getOptionValue={(o: any) => o.id}
                    value={field.value}
                    onChange={field.onChange}
                  />
                </Field>
              )}
            />
          )}

          {promoScope === "PLACE" && (
            <Controller
              name="place"
              control={control}
              render={({ field, fieldState }) => (
                <Field
                  label="Établissement"
                  required
                  error={fieldState.error?.message}
                >
                  <InfiniteScrollSelect
                    url={{ path: "places" }}
                    getOptionLabel={(o: any) => o.name}
                    getOptionValue={(o: any) => o.id}
                    value={field.value}
                    onChange={field.onChange}
                  />
                </Field>
              )}
            />
          )}
        </FormSection>
      )}

      {/* Options */}
      <FormSection title="Options">
        <Controller
          name="isFeatured"
          control={control}
          render={({ field, fieldState }) => (
            <Field
              label="Mis en avant"
              htmlFor="isFeatured"
              required
              error={fieldState.error?.message}
            >
              <CFormSelect
                id="isFeatured"
                {...field}
                value={String(field.value ?? "")}
              >
                <option value="">Sélectionner une option</option>
                <option value="true">Oui</option>
                <option value="false">Non</option>
              </CFormSelect>
            </Field>
          )}
        />

        <div className="col-12 d-flex flex-wrap gap-4 pt-1">
          <Controller
            name="isActive"
            control={control}
            defaultValue={true}
            render={({ field }) => (
              <CFormCheck
                id="isActive"
                label="Actif"
                checked={!!field.value}
                onChange={(e) => field.onChange(e.target.checked)}
              />
            )}
          />
          <Controller
            name="isReusable"
            control={control}
            defaultValue={false}
            render={({ field }) => (
              <CFormCheck
                id="isReusable"
                label="Réutilisable"
                checked={!!field.value}
                onChange={(e) => field.onChange(e.target.checked)}
              />
            )}
          />
        </div>
      </FormSection>
    </>
  );
}

export function ErrorMessage({ error }: { error?: string }) {
  if (!error) return null;
  return <div style={{ color: "red", fontSize: "0.85rem" }}>{error}</div>;
}
